import { NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  CalendarEvent as AngularCalendarEvent,
  CalendarCommonModule,
  CalendarDateFormatter,
  CalendarDayModule,
  CalendarMonthModule,
  CalendarMonthViewBeforeRenderEvent,
  CalendarView,
  CalendarWeekModule,
  CalendarWeekViewBeforeRenderEvent
} from 'angular-calendar';
import { isSameDay, isSameMonth } from 'date-fns';
import { Button } from '@openng/optimus-ui/button';
import { ButtonGroup } from '@openng/optimus-ui/buttongroup';
import { Subject } from 'rxjs';
import { CustomDateFormatter } from '../injectables/custom-date-formatter';

export interface CalendarEvent<MetaType = any> extends AngularCalendarEvent<MetaType> {
  onClick?: (even: CalendarEvent) => any;
}

export interface ViewPeriod {
  start: Date;
  end: Date;
}

@Component({
  selector: 'lib-calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css',
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslocoDirective,
    ButtonGroup,
    Button,
    CalendarCommonModule,
    CalendarMonthModule,
    CalendarWeekModule,
    CalendarDayModule,
    NgClass,
    NgStyle
  ]
})
export class CalendarComponent {
  readonly defaultView = input<CalendarView>(CalendarView.Month);
  readonly events = input<CalendarEvent[]>([]);
  readonly dayStartHour = input<number>(0);
  readonly dayEndHour = input<number>(23);
  readonly weekStartsOn = input<number>(1);

  readonly periodChanged = output<ViewPeriod>();

  readonly view = linkedSignal<CalendarView>(() => this.defaultView());
  readonly viewDate = signal<Date>(new Date());
  readonly activeDayIsOpen = signal<boolean>(false);

  private viewPeriod?: ViewPeriod;

  readonly CalendarView = CalendarView;

  refresh = new Subject<void>();

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate())) {
      if ((isSameDay(this.viewDate(), date) && this.activeDayIsOpen()) || events.length === 0) {
        this.closeOpenMonthViewDay();
      } else {
        this.activeDayIsOpen.set(true);
      }
      this.viewDate.set(date);
    }
  }

  eventClicked(event: CalendarEvent) {
    if (event.onClick) {
      event.onClick(event);
    }
  }

  viewDateChanged(event: Date) {
    this.closeOpenMonthViewDay();
    this.viewDate.set(event);
  }

  setView(view: CalendarView) {
    if (view != CalendarView.Month) {
      this.closeOpenMonthViewDay();
    }
    this.view.set(view);
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen.set(false);
  }

  getCellEvents(events: any) {
    return events.slice(0, 5);
  }

  beforeViewRender(event: CalendarMonthViewBeforeRenderEvent | CalendarWeekViewBeforeRenderEvent) {
    if (
      !this.viewPeriod ||
      !isSameDay(this.viewPeriod.start, event.period.start) ||
      !isSameDay(this.viewPeriod.end, event.period.end)
    ) {
      this.viewPeriod = event.period;
      this.periodChanged.emit(this.viewPeriod);
    }
  }

  dayHeaderClicked(event: Date) {
    this.viewDateChanged(event);
    this.view.set(CalendarView.Day);
  }
}
